import * as m from '@staiter/i18n';
import { ZodError } from 'zod';
import { ApiRequestError, ApiSchemaError } from '~/lib/api/client';
import { captureShapeMismatch } from '~/lib/sentry/capture';

/**
 * E-FE.1 — Canonical error renderer для toast / error-boundary.
 *
 * ВСЕГДА возвращает локализованный {title, body} (никогда JSON.stringify / err.toString /
 * stack-trace). 10 fallback branches:
 *   1. ApiRequestError — prime branch (backend envelope)
 *   2. Response-wrapped {error: ApiError} — unwrap + recurse
 *   3. ZodError — runtime strict-parse fail → error_parse_failed + Sentry
 *   4. AbortError — user cancelled / timeout
 *   5. TypeError "fetch" — network
 *   6. SyntaxError — invalid JSON from server
 *   7. HTTPError-like {status: number}
 *   8. plain Error — generic (НЕ показываем err.message, может быть English)
 *   9. string — generic
 *  10. unknown — error_unknown
 */

export interface ErrorRender {
  title: string;
  body: string;
  trace_id?: string | undefined;
  code?: string | undefined;
}

/**
 * WTU-F7 — вытащить идентификатор обращения из чего угодно, что его несёт.
 *
 * Найдено книгой как «общий рендер теряет ИЗВЕСТНЫЙ `trace_id` в теле тоста»:
 * ветка типизированной ошибки бэкенда его передавала, а все остальные — нет.
 * Между тем `trace_id` приходит в конверте ответа, и объект-обёртка (ветка
 * «HTTPError-like {status}») его несёт: до правки в тело подставлялась пустая
 * строка, и человек, которому сервер уже назвал номер обращения, приходил в
 * поддержку с фразой «Trace:» и пустотой после двоеточия.
 *
 * Пустого «Trace:» больше нет и там, где идентификатора действительно нет:
 * такой случай — клиентское исключение, запроса на сервер не было (класс
 * `client.exception`), и обещать номер обращения нечестно.
 */
function extractTraceId(err: unknown): string | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const o = err as Record<string, unknown>;
  const direct = o.traceId ?? o.trace_id;
  if (typeof direct === 'string' && direct.length > 0) return direct;
  const nested = o.error;
  if (nested && typeof nested === 'object') return extractTraceId(nested);
  return undefined;
}

export function renderApiError(err: unknown): ErrorRender {
  // 1. ApiRequestError — backend envelope prime path.
  if (err instanceof ApiRequestError) {
    return {
      title: m.error_toast_title(),
      body: renderByCode(err.code, err.details ?? {}, err.traceId ?? ''),
      trace_id: err.traceId,
      code: err.code,
    };
  }

  // 2. Response-wrapped {error: ApiError}.
  if (err && typeof err === 'object' && 'error' in err) {
    return renderApiError((err as { error: unknown }).error);
  }

  // S-FIX-2-FE.4.2 — ApiSchemaError: strict client больше не маскирует
  // shape mismatch. Toast показывает "Несовместимый ответ сервера" + path + trace.
  if (err instanceof ApiSchemaError) {
    return {
      title: m.error_schema_mismatch_title(),
      body: m.error_schema_mismatch_body({
        path: `${err.method} ${err.path}`,
        trace: err.traceId ? err.traceId.slice(0, 8) : '—',
      }),
      trace_id: err.traceId,
      code: 'validation.zod_shape_mismatch',
    };
  }

  // 3. ZodError — runtime shape mismatch.
  if (err instanceof ZodError) {
    captureShapeMismatch('renderApiError', err, undefined);
    return {
      title: m.error_toast_title(),
      body: m.error_parse_failed(),
      code: 'validation.zod_shape_mismatch',
    };
  }

  // 4. AbortError (DOMException or plain Error name).
  if (
    (typeof DOMException !== 'undefined' &&
      err instanceof DOMException &&
      err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  ) {
    return {
      title: m.error_toast_title(),
      body: m.error_request_aborted(),
      code: 'request.aborted',
    };
  }

  // 5. Network fetch fail — TypeError с "fetch" в message.
  if (err instanceof TypeError && /fetch/i.test(err.message)) {
    return {
      title: m.error_toast_title(),
      body: m.error_network_unavailable(),
      code: 'network.unavailable',
    };
  }

  // 6. SyntaxError — JSON.parse failure.
  if (err instanceof SyntaxError) {
    return {
      title: m.error_toast_title(),
      body: m.error_parse_failed(),
      code: 'validation.zod_shape_mismatch',
    };
  }

  // 7. HTTPError-like {status}.
  if (
    err &&
    typeof err === 'object' &&
    'status' in err &&
    typeof (err as { status: unknown }).status === 'number'
  ) {
    const code = mapStatusToCode((err as { status: number }).status);
    // WTU-F7 — конверт ответа несёт `trace_id`; до правки он терялся здесь.
    const trace = extractTraceId(err);
    return {
      title: m.error_toast_title(),
      body: renderByCode(code, {}, trace ?? ''),
      code,
      ...(trace ? { trace_id: trace } : {}),
    };
  }

  // 8. plain Error — НЕ показываем err.message (может быть English).
  if (err instanceof Error) {
    // WTU-F7: если носитель всё-таки принёс идентификатор — назвать его.
    const trace = extractTraceId(err);
    return {
      title: m.error_toast_title(),
      body: trace ? m.error_unknown({ trace }) : m.error_unknown_no_trace(),
      code: 'client.exception',
      ...(trace ? { trace_id: trace } : {}),
    };
  }

  // 9. string.
  if (typeof err === 'string') {
    return {
      title: m.error_toast_title(),
      body: m.error_unknown_no_trace(),
      code: 'client.exception',
    };
  }

  // 10. unknown.
  const trace = extractTraceId(err);
  return {
    title: m.error_toast_title(),
    body: trace ? m.error_unknown({ trace }) : m.error_unknown_no_trace(),
    code: 'client.exception',
  };
}

function renderByCode(code: string, details: Record<string, unknown>, trace: string): string {
  switch (code) {
    case 'route.not_found':
      return m.error_route_not_found({
        method: String(details['method'] ?? ''),
        path: String(details['path'] ?? ''),
      });
    case 'route.method_not_allowed':
      return m.error_route_method_not_allowed({ method: String(details['method'] ?? '') });
    case 'route.disabled':
      return m.error_route_disabled();
    case 'auth.missing_token':
      return m.error_auth_missing_token();
    case 'auth.invalid_token':
      return m.error_auth_invalid_token();
    case 'auth.expired_token':
      return m.error_auth_expired_token();
    case 'auth.unauthorized':
      // S-FIX-5-FE.9.2: alias к expired/missing для clarity.
      return m.error_auth_unauthorized();
    case 'auth.forbidden':
      return m.error_auth_forbidden();
    case 'MODULE_FORBIDDEN':
      return m.module_forbidden_desc();
    case 'auth.invalid_credentials':
      return m.error_auth_invalid_credentials();
    case 'validation.invalid_body':
    case 'validation.invalid_query':
    case 'validation.invalid_param':
      return m.error_validation_failed();
    case 'validation.zod_shape_mismatch':
      return m.error_parse_failed();
    case 'validation.precondition_failed':
      return m.error_validation_precondition_failed();
    // project-visibility (F11) — structural-коды видимости с объяснением жеста.
    case 'task_module.mixed_visibility_membership':
      return m.error_task_module_mixed_visibility_membership();
    case 'task_module.inbox_cannot_be_private':
      return m.error_task_module_inbox_cannot_be_private();
    case 'task_module.assignee_not_project_member':
      return m.error_task_module_assignee_not_project_member();
    // project-visibility (B21/F13) — рассекречивание переносом только лидом/админом.
    case 'task_module.move_declassify_lead_required':
      return m.error_move_declassify_lead_required();
    case 'resource.not_found':
      return m.error_resource_not_found();
    case 'resource.conflict':
      return m.error_resource_conflict();
    case 'resource.locked':
      return m.error_resource_locked();
    case 'resource.gone':
      return m.error_resource_gone();
    case 'resource.has_children':
      return m.error_resource_has_children({
        resource_type: String(details['resource_type'] ?? ''),
        count: Number(details['count'] ?? 0),
      });
    case 'resource.has_sle':
      return m.error_resource_has_sle({
        resource_type: String(details['resource_type'] ?? ''),
        count: Number(details['count'] ?? 0),
      });
    case 'resource.has_stock_entries':
      return m.error_resource_has_stock_entries({
        resource_type: String(details['resource_type'] ?? ''),
        count: Number(details['count'] ?? 0),
      });
    case 'resource.has_work_orders':
      return m.error_resource_has_work_orders({
        resource_type: String(details['resource_type'] ?? ''),
        count: Number(details['count'] ?? 0),
      });
    case 'request.timeout':
      return m.error_request_timeout();
    case 'request.aborted':
      return m.error_request_aborted();
    case 'request.body_too_large':
      return m.error_request_body_too_large();
    case 'request.content_type_unsupported':
      return m.error_request_content_type_unsupported();
    case 'rate_limit.exceeded':
      return m.error_rate_limited();
    case 'rate_limit.tenant_quota':
      return m.error_rate_limit_tenant_quota();
    case 'feature.disabled':
      return m.error_feature_disabled();
    case 'maintenance.in_progress':
      return m.error_maintenance_in_progress();
    case 'network.unavailable':
      return m.error_network_unavailable();
    // F2 — синтетический код сторожа paused-мутаций (сервер его не присылает).
    case 'network.offline_paused':
      return m.error_network_offline_paused();
    case 'server.internal':
    case 'server.timeout':
    case 'server.degraded':
      return m.error_server_degraded();
    case 'integration.1c_unreachable':
      return m.error_integration_1c_unreachable();
    case 'integration.bitrix_unreachable':
      return m.error_integration_bitrix_unreachable();
    case 'integration.adesk_unreachable':
      return m.error_integration_adesk_unreachable();
    case 'integration.temporal_unreachable':
      return m.error_integration_temporal_unreachable();
    case 'db.unavailable':
      return m.error_db_unavailable();
    case 'db.constraint_violation':
      return m.error_db_constraint_violation();
    case 'wo.invalid_state_transition':
      return m.error_wo_invalid_state_transition();
    case 'wo.material_shortage':
      return m.error_wo_material_shortage();
    case 'wo.bom_not_found':
      return m.error_wo_bom_not_found();
    case 'stock.insufficient_balance':
      return m.error_stock_insufficient_balance();
    case 'stock.negative_balance_forbidden':
      return m.error_stock_negative_balance_forbidden();
    case 'approval.tier_mismatch':
      return m.error_approval_tier_mismatch();
    case 'approval.approver_insufficient':
      return m.error_approval_approver_insufficient();
    case 'valuation.unknown_method':
      return m.error_valuation_unknown_method();
    case 'task_module.validation': {
      // BE `taskModuleErrors.validation(reason)` → details.reason (enum-код, не freeform).
      const reason = typeof details?.reason === 'string' ? details.reason : '';
      if (reason === 'due_before_start') return m.error_task_module_due_before_start();
      return m.error_task_module_validation();
    }
    default: {
      // Fallback: maybe it's a dot-code not in switch — try m.error_by_code with details.
      const localised = m.error_by_code(code, details);
      if (localised && !localised.startsWith('[missing:')) return localised;
      return m.error_unknown({ trace });
    }
  }
}

function mapStatusToCode(status: number): string {
  if (status === 404) return 'route.not_found';
  if (status === 405) return 'route.method_not_allowed';
  if (status === 401) return 'auth.missing_token';
  if (status === 403) return 'auth.forbidden';
  if (status === 408) return 'request.timeout';
  if (status === 409) return 'resource.conflict';
  if (status === 413) return 'request.body_too_large';
  if (status === 415) return 'request.content_type_unsupported';
  if (status === 422) return 'validation.invalid_body';
  if (status === 429) return 'rate_limit.exceeded';
  if (status === 504) return 'server.timeout';
  if (status === 502 || status === 503) return 'server.degraded';
  if (status >= 500) return 'server.internal';
  return 'server.internal';
}

/** Drop-in replacement для `err.message` / `String(err)` в toast-description. */
export function renderApiErrorMessage(err: unknown): string {
  return renderApiError(err).body;
}
